import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import './Navbar.css';
import favicon from '../../assets/favicon.svg';
import logoPng from '../../assets/log01.png';

const Navbar = ({
  heroRef,
  howItWorksRef,
  featuresRef,
  testimonialsRef,
  faqRef,
  contactRef,
  onSectionClick,
  isStandalonePage
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const navigate = useNavigate();
  const location = useLocation();
  const isBlogPage = location.pathname === '/blog';

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const sections = [
        { ref: heroRef, id: 'hero' },
        { ref: howItWorksRef, id: 'howItWorks' },
        { ref: featuresRef, id: 'features' },
        { ref: testimonialsRef, id: 'testimonials' },
        { ref: faqRef, id: 'faq' },
        { ref: contactRef, id: 'contact' }
      ];

      const currentSection = sections.find(section => {
        if (section.ref?.current) {
          const { top, bottom } = section.ref.current.getBoundingClientRect();
          return top <= 100 && bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [heroRef, howItWorksRef, featuresRef, testimonialsRef, faqRef, contactRef]);

  const handleStartInterview = () => {
    // Call the Google Ads conversion tracking function
    if (typeof window.gtag_report_conversion === 'function') {
      return gtag_report_conversion('/login');
    } else {
      navigate('/login');
    }
  };

  const scrollToSection = (sectionRef, sectionId) => {
    if (isStandalonePage) {
      // If we're on a standalone page (like pricing), navigate back to landing page
      navigate(`/#${sectionId}`);
    } else if (isBlogPage) {
      navigate('/#' + sectionId);
    } else if (sectionRef?.current) {
      const yOffset = -80;
      const element = sectionRef.current;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      
      window.scrollTo({ top: y, behavior: 'smooth' });
      setActiveSection(sectionId);
    }
    setIsMenuOpen(false);
  };

  const navItems = [
    { label: 'How it Works', ref: howItWorksRef, id: 'howItWorks' },
    { label: 'Features', ref: featuresRef, id: 'features' },
    { label: 'FAQ', ref: faqRef, id: 'faq' }
  ];

  // Add handler for pricing navigation
  const handlePricingClick = () => {
    navigate('/pricing');
    setIsMenuOpen(false);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.mobile-nav') && !event.target.closest('.nav-toggle')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <motion.nav
      className={`navbar ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="navbar-container">
        <motion.div
          className="logo-container"
          onClick={() => scrollToSection(heroRef, 'hero')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <img src={favicon} alt="PrepKind Icon" className="logo-icon" />
          <img src={logoPng} alt="PrepKind" className="logo-text-image" />
        </motion.div>

        <div className="nav-links">
          <motion.a
            onClick={() => scrollToSection(howItWorksRef, 'how-it-works')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ cursor: 'pointer' }}
          >
            How it Works
          </motion.a>
          <motion.a
            onClick={() => scrollToSection(featuresRef, 'features')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ cursor: 'pointer' }}
          >
            Features
          </motion.a>
          <motion.a
            onClick={() => scrollToSection(testimonialsRef, 'testimonials')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ cursor: 'pointer' }}
          >
            Testimonials
          </motion.a>
          <motion.a
            onClick={() => scrollToSection(faqRef, 'faq')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ cursor: 'pointer' }}
          >
            FAQ
          </motion.a>
          <motion.a
            onClick={handlePricingClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ cursor: 'pointer' }}
          >
            Pricing
          </motion.a>
          <motion.a
            onClick={() => navigate('/blog')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="blog-link"
          >
            Blog
          </motion.a>
          <motion.button
            className="landing-page-login-btn"
            onClick={handleStartInterview}
          >
            Login
          </motion.button>
        </div>

        <div className="mobile-header-actions">
          <motion.button
            className="mobile-login-btn"
            onClick={handleStartInterview}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Login
          </motion.button>
        </div>

        <motion.button
          className="nav-toggle"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileTap={{ scale: 0.9 }}
          aria-label="Toggle mobile menu"
        >
          <div className={`hamburger ${isMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </motion.button>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="mobile-nav"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="nav-box">
              <motion.a
                onClick={() => scrollToSection(featuresRef, 'features')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Features
              </motion.a>
              <motion.a
                onClick={() => scrollToSection(howItWorksRef, 'how-it-works')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                How it Works
              </motion.a>
              <motion.a
                onClick={() => scrollToSection(testimonialsRef, 'testimonials')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Testimonials
              </motion.a>
              <motion.a
                onClick={() => scrollToSection(faqRef, 'faq')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                FAQ
              </motion.a>
              <motion.a
                onClick={handlePricingClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Pricing
              </motion.a>
              <motion.a
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate('/blog');
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="blog-link"
              >
                Blog
              </motion.a>
            </div>
            <motion.button
              className="dropdown-start-interview-btn"
              onClick={handleStartInterview}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Interview
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;